import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });
  const [consent, setConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = 'service_18hbr8l';
  const EMAILJS_TEMPLATE_ID = 'template_bogm2bd';
  const EMAILJS_PUBLIC_KEY = 'Puxx0NSjhHF3G1S6F';

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init({
      publicKey: EMAILJS_PUBLIC_KEY,
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Remove all non-digit characters and limit to 10 digits
      const cleaned = value.replace(/\D/g, '').substring(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (phoneNumber.length === 0) return true;
    if (phoneNumber.length !== 10) return false;
    
    const firstDigit = phoneNumber.charAt(0);
    return ['6', '7', '8', '9'].includes(firstDigit);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (!validatePhoneNumber(formData.phone)) {
      showMessage('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.', 'error');
      return;
    }

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      showMessage('Please fill in all required fields.', 'error');
      return;
    }

    if (!consent) {
      showMessage('Please consent to data processing.', 'error');
      return;
    }

    setIsLoading(true);

    // Template parameters for EmailJS
    const templateParams = {
      user_name: formData.name,
      user_email: formData.email,
      phone_number: '+91' + formData.phone,
      subject: formData.subject,
      message: formData.message,
      to_email: 'prajapatiishwar79@gmail.com'
    };

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        {
          publicKey: EMAILJS_PUBLIC_KEY,
        }
      );

      if (response.status === 200) {
        showMessage('Thank you! Your message has been sent successfully. We will get back to you within 24 hours.', 'success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'general',
          message: ''
        });
        setConsent(false);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      showMessage('Sorry, there was an error sending your message. Please try again or contact us directly.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p>We're here to help you with all your insurance needs</p>
        </div>
      </section>

      {/* Contact Content */}
      <div id="contactContent" className="contact-content">
        {/* Contact Information */}
        <section className="contact-info">
          <div className="container">
            <div className="contact-grid">
              {/* Phone */}
              <div className="contact-card">
                <div className="contact-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M22 16.92V19.92C22 20.52 21.52 21 20.92 21C9.4 21 0 11.6 0 0.08C0 -0.52 0.48 -1 1.08 -1H4.08C4.68 -1 5.16 -0.52 5.16 0.08C5.16 1.68 5.44 3.24 5.96 4.68C6.08 5 5.96 5.36 5.68 5.6L3.44 7.84C5.28 11.48 8.52 14.72 12.16 16.56L14.4 14.32C14.64 14.04 15 13.92 15.32 14.04C16.76 14.56 18.32 14.84 19.92 14.84C20.52 14.84 21 15.32 21 15.92V18.92C21 19.52 20.52 20 19.92 20Z"
                      fill="#0057A4"
                    />
                  </svg>
                </div>
                <h3>Phone</h3>
                <p><a href="tel:+919876543210">+91 98765 43210</a></p>
                <p><a href="tel:+919876543211">+91 98765 43211</a></p>
                <small>Mon-Fri: 9AM-6PM, Sat: 9AM-2PM</small>
              </div>

              {/* Email */}
              <div className="contact-card">
                <div className="contact-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                      stroke="#0057A4"
                      strokeWidth="2"
                    />
                    <path d="M22 6L12 13L2 6" stroke="#0057A4" strokeWidth="2" />
                  </svg>
                </div>
                <h3>Email</h3>
                <p>
                  <a href="mailto:info@wecareassurance.com">info@wecareassurance.com</a>
                </p>
                <p>
                  <a href="mailto:claims@wecareassurance.com">claims@wecareassurance.com</a>
                </p>
                <small>24/7 Email Support</small>
              </div>

              {/* Address */}
              <div className="contact-card">
                <div className="contact-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 10C21 17 12 23 12 23S3 17 3 10C3 5 7 1 12 1S21 5 21 10Z"
                      stroke="#0057A4"
                      strokeWidth="2"
                    />
                    <circle
                      cx="12"
                      cy="10"
                      r="3"
                      stroke="#0057A4"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <h3>Our Offices</h3>
                <p>
                  <strong>Anand:</strong><br />
                  F-4/5 City Plaza, Nr. Baliyadev Crossing, Nr. Sardargunj,<br />
                  Anand – 388001
                </p>
                <p>
                  <strong>Vadodara:</strong><br />
                  504, The Park, Akshar Chowk, Old Padra Road, Vadodara-390020
                </p>
                <small>Visit us during business hours</small>
              </div>

              {/* Office Numbers */}
              <div className="contact-card">
                <div className="contact-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M22 16.92V19.92C22 20.52 21.52 21 20.92 21C9.4 21 0 11.6 0 0.08C0 -0.52 0.48 -1 1.08 -1H4.08C4.68 -1 5.16 -0.52 5.16 0.08C5.16 1.68 5.44 3.24 5.96 4.68C6.08 5 5.96 5.36 5.68 5.6L3.44 7.84C5.28 11.48 8.52 14.72 12.16 16.56L14.4 14.32C14.64 14.04 15 13.92 15.32 14.04C16.76 14.56 18.32 14.84 19.92 14.84C20.52 14.84 21 15.32 21 15.92V18.92C21 19.52 20.52 20 19.92 20Z"
                      fill="#0057A4"
                    />
                  </svg>
                </div>
                <h3>Office Numbers</h3>
                <p><a href="tel:+919723225401">Corporate: 9723225401</a></p>
                <p><a href="tel:+917016091950">Motor Claim: 7016091950</a></p>
                <p><a href="tel:+917284047322">Health Claim: 7284047322</a></p>
                <small>Direct Call Support</small>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Contact Form */}
      <section className="contact-form-section">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you within 24 hours</p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <div className={`phone-input-container ${!validatePhoneNumber(formData.phone) && formData.phone ? 'error' : ''}`}>
                    <span className="country-code">+91</span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder=""
                      maxLength="10"
                      required
                    />
                  </div>
                  <small className="phone-helper">Enter 10-digit mobile number</small>
                  {!validatePhoneNumber(formData.phone) && formData.phone && (
                    <span className="phone-error">Mobile number should start with 6, 7, 8, or 9</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select 
                    id="subject" 
                    name="subject" 
                    value={formData.subject}
                    onChange={handleInputChange}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="claims">Claims Support</option>
                    <option value="policy">Policy Information</option>
                    <option value="complaint">Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Please describe your inquiry or concern in detail..."
                  required
                ></textarea>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    id="consent" 
                    name="consent" 
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required 
                  />
                  <span className="checkmark"></span>
                  I consent to WE CARE ASSURANCE processing my personal data for
                  the purpose of responding to my inquiry
                </label>
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading}>
                <span className="btn-text" style={{ display: isLoading ? 'none' : 'inline' }}>
                  Send Message
                </span>
                <span className="btn-loading" style={{ display: isLoading ? 'flex' : 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="3" fill="currentColor">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        values="0 12 12;360 12 12"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                  Sending...
                </span>
              </button>

              {message.text && (
                <div className={`form-message ${message.type}`} style={{ display: 'block' }}>
                  {message.text}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title">Visit Our Branches</h2>

          <div className="map-grid">
            {/* Main Office */}
            <div className="map-container">
              <h3 className="branch-title">Main Office</h3>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d472120.2380113533!2d72.53065833462587!3d22.412483257773907!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e4f1bb7a6135d%3A0xa2ee8216860f1706!2sWe%20Care%20Assurance%20LLP!5e0!3m2!1sen!2sin!4v1756052733244!5m2!1sen!2sin"
                width="100%"
                height="350"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Main Office Location"
              ></iframe>
            </div>

            {/* Branch Office */}
            <div className="map-container">
              <h3 className="branch-title">Branch Office</h3>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.555930973703!2d73.16207477434452!3d22.294805343096364!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395fc901e1815611%3A0x325de8c1e4967ee5!2sWe%20Care%20Freedom%20Wealth%20Pvt.%20Ltd!5e0!3m2!1sen!2sin!4v1756052540202!5m2!1sen!2sin"
                width="100%"
                height="350"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Branch Office Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button 
        id="backToTop" 
        title="Go to top" 
        style={{ display: showBackToTop ? 'block' : 'none' }}
        onClick={scrollToTop}
      >
        ↑
      </button>
    </>
  );
};

export default ContactPage;
